import { useState, useEffect, useRef } from "react";
import { HiX, HiPlus } from "react-icons/hi";
import { PiCaretDownBold, PiDownloadSimpleBold, PiFileTextBold, PiClockCounterClockwise, PiScanBold, PiUploadSimpleBold, PiPushPinBold } from "react-icons/pi";
import { useAutoAnimate } from "@formkit/auto-animate/react";

import DateInput from "../DateInput";
import TimeInput from "../TimeInput";
import QuestionSearchInput from "./QuestionSearchInput";
import API from "../../api";
import toast from "react-hot-toast";
import { capture } from "../../instrument";
import { DWT_INSTALLER_URL, isScannerServiceMissing, loadScannerList, scanToJpeg, SCANNER_STORAGE_KEY } from "../../utils/dynamsoftWebTwain";
import { NON_COMMUNIQUE } from "../../utils";

const getCurrentSegment = (answer) => {
  if (!answer) return "";
  const parts = answer.split(";");
  return parts[parts.length - 1].trim();
};

const compareByOrder = (a, b) => {
  if (a.order == null) return b.order == null ? 0 : 1;
  if (b.order == null) return -1;
  return a.order < b.order ? -1 : a.order > b.order ? 1 : 0;
};

const formatAnswerDisplay = (answer, questionRecord) => {
  if (answer === NON_COMMUNIQUE) return "Non communiqué";
  if (questionRecord.answerType !== "MULTIPLE_CHOICE") return answer;
  const label = questionRecord.options?.find((o) => o.optionId?.toString() === answer?.toString())?.label;
  return label || answer;
};

const QuestionsSection = ({
  questionRecords = [],
  loading = false,
  isLocked = false,
  showProductionRequired = false,
  onQuestionRecordChange,
  onScrollToImage,
  ProcessingStatusIcon,
  getAdmissionDetails,
  setLoading,
  selectedAdmissionId,
}) => {
  const [openAutocompleteIndex, setOpenAutocompleteIndex] = useState(null);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [historyExpandedCode, setHistoryExpandedCode] = useState(null);
  const [questionRecordHistory, setQuestionRecordHistory] = useState({});
  const [scanningQuestionRecordId, setScanningQuestionRecordId] = useState(null);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [scanners, setScanners] = useState([]);
  const [scannerName, setScannerName] = useState(() => localStorage.getItem(SCANNER_STORAGE_KEY) || "");
  const [serviceNotInstalled, setServiceNotInstalled] = useState(false);
  const scannersRequestedRef = useRef(false);
  const [animateParentRef] = useAutoAnimate();

  const ensureScanners = () => {
    if (scannersRequestedRef.current) return;
    scannersRequestedRef.current = true;
    loadScannerList().then(({ scanners: nextScanners, scannerName: nextScannerName, serviceNotInstalled: notInstalled }) => {
      setScanners(nextScanners);
      setScannerName((current) => (nextScanners.includes(current) ? current : nextScannerName));
      setServiceNotInstalled(notInstalled);
    });
  };

  const onScannerChange = (event) => {
    const name = event.target.value;
    setScannerName(name);
    localStorage.setItem(SCANNER_STORAGE_KEY, name);
  };

  useEffect(() => {
    const fetchHistory = async () => {
      if (!selectedAdmissionId || !questionRecords?.length) {
        setQuestionRecordHistory({});
        return;
      }

      const patientId = questionRecords[0]?.patientId;
      const indexName = questionRecords[0]?.indexName;
      if (!patientId || !indexName) return;

      const questionCodes = questionRecords
        .map((qr) => qr.questionCode)
        .filter(Boolean)
        .join(",");
      const excludeIds = questionRecords.map((qr) => qr._id).join(",");

      try {
        const { ok, data } = await API.get(`/admin/question-record?patientId=${patientId}&indexName=${indexName}&questionCodes=${questionCodes}&excludeIds=${excludeIds}`);
        if (!ok) throw new Error("Erreur lors de la récupération des question records");

        const grouped = {};
        for (const record of data) {
          if (!record.questionCode || !record.answer) continue;
          if (!grouped[record.questionCode]) grouped[record.questionCode] = [];
          grouped[record.questionCode].push(record);
        }

        setQuestionRecordHistory(grouped);
      } catch (error) {
        capture(error);
      }
    };

    fetchHistory();
  }, [selectedAdmissionId, questionRecords?.map((qr) => qr._id?.toString()).join(",")]);

  const onQuestionRecordDocumentUpload = async (questionRecordId, file) => {
    setLoading(true);
    try {
      const formData = new FormData();

      let questionRecordData = questionRecords.find((qr) => qr._id === questionRecordId);
      if (!questionRecordData) throw new Error("QuestionRecord non trouvé");
      questionRecordData = JSON.parse(JSON.stringify(questionRecordData));

      if (file !== null) {
        const acceptedTypes = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
        if (!acceptedTypes.includes(file.type)) {
          toast.error("Format de fichier non supporté. Veuillez utiliser une image (PNG, JPG) ou un PDF.");
          return setLoading(false);
        }
        formData.append(`document-${questionRecordData.questionId}`, file);
      } else {
        questionRecordData.answer = "";
      }

      formData.append("data", JSON.stringify({ questionRecords: [questionRecordData] }));

      const response = await API.putFormData(`/admin/admission/${selectedAdmissionId}`, formData);

      if (!response.ok) throw new Error(response.message);

      await getAdmissionDetails(selectedAdmissionId);
    } catch (error) {
      capture(error);
      toast.error("Une erreur est survenue lors du téléchargement du document");
    }
    setLoading(false);
  };

  const onScanQuestionRecordDocument = async (questionRecordId) => {
    ensureScanners();
    setScanningQuestionRecordId(questionRecordId);
    try {
      const file = await scanToJpeg(scannerName);
      if (!file) {
        toast.error("Aucune page numérisée");
        return;
      }
      await onQuestionRecordDocumentUpload(questionRecordId, file);
    } catch (error) {
      if (isScannerServiceMissing(error)) {
        setServiceNotInstalled(true);
        toast.error("Service de numérisation non installé");
      } else {
        capture(error);
        toast.error(error.message || "Erreur lors du scan");
      }
    } finally {
      setScanningQuestionRecordId(null);
    }
  };

  const onAddQuestion = async (question) => {
    if (!question?._id) return;
    setLoading(true);
    try {
      const { ok, message } = await API.post(`/admin/admission/${selectedAdmissionId}/question-record`, { questionId: question._id });
      if (!ok) throw new Error(message);
      setIsAddingQuestion(false);
      await getAdmissionDetails(selectedAdmissionId);
      toast.success("Question ajoutée");
    } catch (error) {
      capture(error);
      toast.error(error.message || "Une erreur est survenue lors de l'ajout de la question");
    }
    setLoading(false);
  };

  const onUnforceQuestion = async (questionRecordId) => {
    setLoading(true);
    try {
      const { ok, message } = await API.put(`/admin/admission/${selectedAdmissionId}/question-record/${questionRecordId}`, { forced: false });
      if (!ok) throw new Error(message);
      await getAdmissionDetails(selectedAdmissionId);
      toast.success("Ajout manuel retiré");
    } catch (error) {
      capture(error);
      toast.error(error.message || "Une erreur est survenue");
    }
    setLoading(false);
  };

  return (
    <>
      <div className="mb-2">
        <div className="section-title">
          <span>Questions</span>
          <span className="meta-badge bg-gray-100 text-gray-500">{questionRecords.length}</span>
        </div>
      </div>

      <div ref={animateParentRef} className="card divide-y divide-gray-100">
        {questionRecords.length === 0 && <div className="px-4 py-3 text-[13px] italic text-gray-400">Aucune question</div>}
        {[...questionRecords].sort(compareByOrder).map((questionRecord) => {
          const index = questionRecords.findIndex((qr) => qr._id === questionRecord._id);
          const questionText = questionRecord.question || "";
          const answerOptions = (questionRecord.options || []).map((option) => ({ optionId: option.optionId, value: option.label || "" }));
          const isProductionOnly = Boolean(questionRecord.patient || questionRecord.ide);
          const isMissing =
            !isLocked && !questionRecord.answer && !questionRecord.autoAnswer && questionRecord.isRequired !== false && (!isProductionOnly || showProductionRequired);
          const missingState = isMissing ? "required" : undefined;
          const isNonCommunique = questionRecord.answer === NON_COMMUNIQUE;
          const onNonCommuniqueKeyDown = (e) => {
            if (e.key.toLowerCase() !== "n" || e.ctrlKey || e.metaKey || e.altKey) return;
            e.preventDefault();
            onQuestionRecordChange(index, NON_COMMUNIQUE);
          };
          const history = questionRecordHistory?.[questionRecord.questionCode] || [];
          const isHistoryOpen = historyExpandedCode === questionRecord.questionCode;

          return (
            <div key={questionRecord._id} className="px-4 py-2.5" data-focus-field={`questionRecords.${questionRecord._id}`}>
              <div className="mb-1 flex items-start justify-between gap-2">
                <div className="min-w-0 px-2">
                  {(questionRecord.questionCode || questionRecord.autoAnswer) && (
                    <div className="mb-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
                      {questionRecord.questionCode && <span className="font-mono text-[11px] leading-tight text-gray-400">{questionRecord.questionCode}</span>}
                      {questionRecord.autoAnswer && (
                        <span className="inline-flex items-center rounded-full border border-green-200 bg-green-50 px-1.5 text-[10px] font-medium leading-tight text-green-700">
                          Auto
                        </span>
                      )}
                    </div>
                  )}
                  <span className={`text-[13px] font-semibold leading-snug ${isMissing ? "text-orange-600" : "text-gray-800"}`}>
                    {questionText}
                    {ProcessingStatusIcon && (
                      <span className="ml-1 inline-flex align-middle">
                        <ProcessingStatusIcon type={questionRecord.answerType === "DOCUMENT" ? "FILE" : "QUESTION"} referenceId={questionRecord._id} className="h-3 w-3" />
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {questionRecord.forced && isLocked && (
                    <span
                      title="Ajoutée manuellement"
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-1.5 text-[10px] font-medium leading-tight text-gray-500"
                    >
                      <PiPushPinBold className="h-2.5 w-2.5" />
                      Manuel
                    </span>
                  )}
                  {questionRecord.forced && !isLocked && (
                    <button
                      type="button"
                      onClick={() => onUnforceQuestion(questionRecord._id)}
                      disabled={loading}
                      title="Ajoutée manuellement — cliquer pour retirer"
                      className="group inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-1.5 text-[10px] font-medium leading-tight text-gray-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <PiPushPinBold className="h-2.5 w-2.5 group-hover:hidden" />
                      <HiX className="hidden h-2.5 w-2.5 group-hover:block" />
                      Manuel
                    </button>
                  )}
                  {history.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setHistoryExpandedCode((prev) => (prev === questionRecord.questionCode ? null : questionRecord.questionCode))}
                      className={`icon-chip-button text-gray-400 hover:text-gray-600 ${isHistoryOpen ? "bg-gray-100 text-gray-600" : ""}`}
                      title="Historique des réponses"
                    >
                      <PiClockCounterClockwise className="h-3.5 w-3.5" />
                      <span className="text-[11px] font-medium">{history.length}</span>
                    </button>
                  )}
                </div>
              </div>

              <div>
                {questionRecord.answerType === "MULTIPLE_CHOICE" ? (
                  <select
                    className={`ghost-input ${questionRecord.answer ? "text-gray-800" : isMissing ? "text-orange-500" : "text-gray-300"}`}
                    data-state={missingState}
                    value={questionRecord.answer || ""}
                    onChange={(e) => onQuestionRecordChange(index, e.target.value)}
                  >
                    <option value="" className="text-gray-300">
                      Sélectionner une réponse
                    </option>
                    {answerOptions.map((option, optIndex) => (
                      <option key={optIndex} value={option.optionId} className="text-gray-800">
                        {option.value}
                      </option>
                    ))}
                  </select>
                ) : questionRecord.answerType === "TEXT" ? (
                  <div className="relative">
                    <input
                      type="text"
                      className="ghost-input"
                      data-state={missingState}
                      value={questionRecord.answer || ""}
                      placeholder="Saisir une réponse..."
                      onChange={(e) => {
                        onQuestionRecordChange(index, e.target.value);
                        if (answerOptions && answerOptions.length > 0) {
                          setOpenAutocompleteIndex(index);
                          setSelectedSuggestionIndex(0);
                        }
                      }}
                      onFocus={() => {
                        if (answerOptions && answerOptions.length > 0) {
                          setOpenAutocompleteIndex(index);
                          setSelectedSuggestionIndex(0);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setOpenAutocompleteIndex(null);
                          setSelectedSuggestionIndex(0);
                        }, 200);
                      }}
                      onKeyDown={(e) => {
                        if (answerOptions && answerOptions.length > 0 && openAutocompleteIndex === index) {
                          const currentSegment = getCurrentSegment(questionRecord.answer);
                          const filtered = answerOptions.filter((s) => !currentSegment || s.value.toLowerCase().includes(currentSegment.toLowerCase()));

                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setSelectedSuggestionIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
                          } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : 0));
                          } else if (e.key === "Enter" && filtered.length > 0) {
                            e.preventDefault();
                            const selectedOption = filtered[selectedSuggestionIndex];
                            if (selectedOption) {
                              const existingParts = (questionRecord.answer || "")
                                .split(";")
                                .slice(0, -1)
                                .map((p) => p.trim())
                                .filter(Boolean);
                              const newAnswer = [...existingParts, selectedOption.value].join("; ") + "; ";
                              onQuestionRecordChange(index, newAnswer);
                              setSelectedSuggestionIndex(0);
                            }
                          } else if (e.key === "Escape") {
                            setOpenAutocompleteIndex(null);
                            setSelectedSuggestionIndex(0);
                          }
                        }
                      }}
                    />
                    {answerOptions &&
                      answerOptions.length > 0 &&
                      openAutocompleteIndex === index &&
                      (() => {
                        const currentSegment = getCurrentSegment(questionRecord.answer);
                        const filtered = answerOptions.filter((s) => !currentSegment || s.value.toLowerCase().includes(currentSegment.toLowerCase()));
                        if (filtered.length === 0) return null;
                        return (
                          <div className="dropdown-panel absolute z-50 mt-1 max-h-60 w-full overflow-auto">
                            {filtered.map((option, sugIndex) => (
                              <button
                                key={sugIndex}
                                type="button"
                                className={`w-full px-3 py-2 text-left text-[13px] transition-colors focus:outline-none ${
                                  sugIndex === selectedSuggestionIndex ? "bg-primary-50 text-primary-700" : "text-gray-700 hover:bg-gray-50"
                                }`}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  const existingParts = (questionRecord.answer || "")
                                    .split(";")
                                    .slice(0, -1)
                                    .map((p) => p.trim())
                                    .filter(Boolean);
                                  const newAnswer = [...existingParts, option.value].join("; ") + "; ";
                                  onQuestionRecordChange(index, newAnswer);
                                  setSelectedSuggestionIndex(0);
                                }}
                                onMouseEnter={() => setSelectedSuggestionIndex(sugIndex)}
                              >
                                {option.value}
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                  </div>
                ) : questionRecord.answerType === "DATE" || questionRecord.answerType === "HOUR" ? (
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      {isNonCommunique ? (
                        <span
                          className="flex h-8 items-center px-2 text-[13px] italic text-gray-600"
                          onClick={() => {
                            onQuestionRecordChange(index, "");
                            setTimeout(() => document.querySelector(`#question-${index}`)?.focus(), 50);
                          }}
                        >
                          Non communiqué
                        </span>
                      ) : questionRecord.answerType === "DATE" ? (
                        <DateInput
                          ghost
                          value={questionRecord.answer || ""}
                          onChange={(date) => onQuestionRecordChange(index, date)}
                          isRequiredEmpty={isMissing}
                          onKeyDown={onNonCommuniqueKeyDown}
                        />
                      ) : (
                        <TimeInput
                          id={`question-${index}`}
                          value={questionRecord.answer || ""}
                          onChange={(time) => onQuestionRecordChange(index, time)}
                          data-state={missingState}
                          onKeyDown={onNonCommuniqueKeyDown}
                        />
                      )}
                    </div>
                  </div>
                ) : questionRecord.answerType === "DOCUMENT" ? (
                  questionRecord.documentUrl || questionRecord.answer ? (
                    <div
                      onClick={() => onScrollToImage?.(questionRecord._id)}
                      className={`flex h-8 cursor-pointer items-center justify-between gap-2 rounded-md border px-2 transition-colors ${
                        isMissing ? "border-orange-300 bg-orange-50" : "border-gray-200 hover:border-primary-300 hover:bg-primary-50"
                      }`}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <PiFileTextBold className="h-3.5 w-3.5 shrink-0 text-primary-500" />
                        <span className="truncate text-[13px] font-medium text-gray-700">{questionRecord.questionCode}</span>
                      </div>
                      {!isLocked && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onQuestionRecordDocumentUpload(questionRecord._id, null);
                          }}
                          className="text-gray-400 transition-colors hover:text-red-500"
                        >
                          <HiX className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ) : (
                    (() => {
                      const isScanning = scanningQuestionRecordId === questionRecord._id;
                      const isBusy = loading === true || scanningQuestionRecordId !== null;
                      const isScanDisabled = isBusy;
                      return (
                        <div
                          className={`flex h-8 w-full items-center gap-2 rounded-md border border-dashed px-2 transition-colors ${
                            isMissing ? "border-orange-300 bg-orange-50" : "border-gray-300 bg-white"
                          }`}
                        >
                          <span className={`flex-1 truncate text-[12px] italic ${isMissing ? "text-orange-600" : "text-gray-400"}`}>
                            {isScanning ? "Scan en cours…" : "Aucun document"}
                          </span>
                          {!isLocked && scanners.length > 1 && (
                            <div className="relative">
                              <select
                                className="max-w-[110px] cursor-pointer appearance-none truncate bg-transparent pr-3.5 text-[11px] text-gray-500 hover:text-primary-600 focus:outline-none disabled:opacity-50"
                                value={scannerName}
                                onChange={onScannerChange}
                                disabled={isBusy}
                                aria-label="Changer de scanner"
                              >
                                {scanners.map((name) => (
                                  <option key={name} value={name}>
                                    {name}
                                  </option>
                                ))}
                              </select>
                              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
                                <PiCaretDownBold className="h-2.5 w-2.5 text-gray-400" />
                              </div>
                            </div>
                          )}
                          {!isLocked && (
                            <>
                              {serviceNotInstalled ? (
                                <a
                                  href={DWT_INSTALLER_URL}
                                  title="Installer le driver du scanner"
                                  className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-primary-600 transition-colors hover:bg-primary-50"
                                >
                                  <PiDownloadSimpleBold className="h-3.5 w-3.5" />
                                  <span>Installer le driver</span>
                                </a>
                              ) : (
                                <button
                                  type="button"
                                  title="Numériser un document"
                                  className="rounded p-1 text-gray-500 transition-colors hover:bg-primary-50 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                                  onClick={() => onScanQuestionRecordDocument(questionRecord._id)}
                                  disabled={isScanDisabled}
                                >
                                  <PiScanBold className="h-3.5 w-3.5" />
                                </button>
                              )}
                              <label
                                title="Téléverser un document"
                                className={`rounded p-1 text-gray-500 transition-colors hover:bg-primary-50 hover:text-primary-600 ${
                                  isBusy ? "cursor-not-allowed opacity-40" : "cursor-pointer"
                                }`}
                              >
                                <PiUploadSimpleBold className="h-3.5 w-3.5" />
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/jpg,application/pdf"
                                  className="hidden"
                                  disabled={isBusy}
                                  onChange={(e) => {
                                    const selectedFile = e.target.files?.[0];
                                    if (selectedFile) {
                                      onQuestionRecordDocumentUpload(questionRecord._id, selectedFile);
                                    }
                                    e.target.value = "";
                                  }}
                                />
                              </label>
                            </>
                          )}
                        </div>
                      );
                    })()
                  )
                ) : null}
              </div>

              {isHistoryOpen && history.length > 0 && (
                <div className="mt-2 overflow-hidden rounded-md border border-gray-100 bg-gray-50/60">
                  <div className="divide-y divide-gray-100">
                    {history.slice(0, 5).map((entry, i) => {
                      const hasAnswer = entry.answer !== null && entry.answer !== undefined && entry.answer !== "";
                      const isDocumentType = questionRecord.answerType === "DOCUMENT";
                      const displayAnswer = hasAnswer
                        ? isDocumentType
                          ? "Document téléchargé"
                          : formatAnswerDisplay(entry.answer, entry)
                        : isDocumentType
                          ? "Aucun document"
                          : "Non renseigné";

                      return (
                        <div key={i} className="flex items-center gap-3 px-3 py-1.5">
                          <span className="w-16 shrink-0 text-[10px] font-medium uppercase tracking-[0.06em] text-gray-400">
                            {new Date(entry.answeredAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                          </span>
                          <span className={`flex-1 text-[13px] ${hasAnswer ? "text-gray-800" : "italic text-gray-400"}`}>{displayAnswer}</span>
                        </div>
                      );
                    })}
                  </div>
                  {history.length > 5 && (
                    <div className="border-t border-gray-100 bg-gray-100/70 px-3 py-1.5 text-[11px] text-gray-500">
                      +{history.length - 5} réponse{history.length - 5 > 1 ? "s" : ""} non affichée{history.length - 5 > 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isLocked && (
        <div className="mt-2">
          {isAddingQuestion ? (
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <QuestionSearchInput onSelect={onAddQuestion} disabled={loading} />
              </div>
              <button
                type="button"
                onClick={() => setIsAddingQuestion(false)}
                className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-gray-400 transition-colors hover:text-gray-600"
              >
                Annuler
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsAddingQuestion(true)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-primary-50 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <HiPlus className="h-3.5 w-3.5" />
              Ajouter une question
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default QuestionsSection;
